import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BlueprintLabel } from '../components/nm/BlueprintLabel';
import { SmokeProgressDialog } from '../components/SmokeProgressDialog';
import {
  clearTemplateQa,
  comboDisplayLabel,
  downloadTemplateQaRunBundle,
  fetchRunManifestResult,
  fetchTemplateQaHealth,
  fetchTemplateQaRun,
  fetchTemplateQaRuns,
  fetchTemplateQaStatus,
  manifestFromProgress,
  progressCounts,
  startTemplateQa,
  templateQaImageUrl,
  templateQaRunGalleryUrl,
  type TemplateQaHealth,
  type TemplateQaManifestEntry,
  type TemplateQaProgressFile,
  type TemplateQaRunSummary,
} from '../lib/templateQaClient';

const LIVE = '__live__';

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${s}s`;
}

function formatRunTime(iso?: string): string {
  if (!iso) return 'unknown time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function runOptionLabel(r: TemplateQaRunSummary): string {
  const counts = `${r.ok ?? 0}/${r.total ?? 0} ok${r.fail ? `, ${r.fail} fail` : ''}`;
  const kind = r.smoke ? 'smoke' : 'full';
  return `${formatRunTime(r.finishedAt ?? r.startedAt)} · ${counts} · ${kind}`;
}

function progressLabel(p: TemplateQaProgressFile | null): string {
  if (!p) return '';
  const fromCombo = comboDisplayLabel(p.currentCombo);
  if (fromCombo) return fromCombo;
  if (p.message) return p.message;
  return '';
}

type ResultsFilter = 'all' | 'failed';
type GroupMode = 'industry' | 'package';

export function TemplateQaPage() {
  const [orchestratorOk, setOrchestratorOk] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [progress, setProgress] = useState<TemplateQaProgressFile | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  const [runs, setRuns] = useState<TemplateQaRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [archivedManifest, setArchivedManifest] = useState<TemplateQaManifestEntry[]>([]);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [health, setHealth] = useState<TemplateQaHealth | null>(null);
  const [downloadingRun, setDownloadingRun] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(0);
  const [imgFailed, setImgFailed] = useState(0);
  const [filter, setFilter] = useState<ResultsFilter>('all');
  const [groupMode, setGroupMode] = useState<GroupMode>('industry');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ entry: TemplateQaManifestEntry; runId: string; version?: string } | null>(
    null,
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      const { runs: list } = await fetchTemplateQaRuns();
      setRuns(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      setHealth(await fetchTemplateQaHealth());
    } catch {
      setHealth(null);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await fetchTemplateQaStatus();
      setOrchestratorOk(true);
      setError(null);
      if (status.running && status.activeRunId) {
        setRunning(true);
        setRunId(status.activeRunId);
        setProgress(status.progress);
        setSelectedRunId(LIVE);
        setStartedAt((prev) => prev ?? Date.now());
        setDialogOpen(true);
      }
      await loadRuns();
      await loadHealth();
    } catch (e) {
      setOrchestratorOk(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [loadRuns, loadHealth]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!running || !runId) return;
    const poll = async () => {
      try {
        const data = await fetchTemplateQaRun(runId);
        setProgress(data.progress);
        const st = data.progress?.status;
        const orch = data.orchestratorStatus;
        const finished =
          st === 'done' ||
          st === 'done_with_failures' ||
          st === 'failed' ||
          (orch !== 'running' && orch !== 'idle' && orch !== 'starting');
        if (finished) {
          setRunning(false);
          if (pollRef.current) clearInterval(pollRef.current);
          const list = await loadRuns();
          void loadHealth();
          // Pin the just-finished run in the picker.
          if (list.some((r) => r.runId === runId)) setSelectedRunId(runId);
          const { failed } = progressCounts(data.progress);
          if (failed > 0) setFilter('failed');
          setDialogOpen(true);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setRunning(false);
        setDialogOpen(true);
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };
    void poll();
    pollRef.current = setInterval(() => void poll(), 1500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [running, runId, loadRuns, loadHealth]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Load an archived run's manifest when one is selected (and it isn't the live run).
  useEffect(() => {
    setManifestError(null);
    if (!selectedRunId || selectedRunId === LIVE) {
      setArchivedManifest([]);
      return;
    }
    let cancelled = false;
    void fetchRunManifestResult(selectedRunId).then((res) => {
      if (cancelled) return;
      setArchivedManifest(res.entries);
      setManifestError(res.error);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  // Reset the image-load tally whenever the viewed run changes.
  useEffect(() => {
    setImgLoaded(0);
    setImgFailed(0);
  }, [selectedRunId, runId]);

  const handleStart = async () => {
    setError(null);
    setFilter('all');
    setStartedAt(Date.now());
    setProgress({ status: 'starting', current: 0, total: 0 });
    setSelectedRunId(LIVE);
    setDialogOpen(true);
    try {
      const { runId: id, total } = await startTemplateQa({ smoke: true });
      setRunId(id);
      setRunning(true);
      setProgress((prev) => ({
        status: 'starting',
        current: 0,
        total: total ?? prev?.total ?? 0,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRunning(false);
      setStartedAt(null);
    }
  };

  const handleClear = async () => {
    setError(null);
    try {
      await clearTemplateQa();
      setProgress(null);
      setRunId(null);
      setSelectedRunId(null);
      setStartedAt(null);
      setFilter('all');
      setManifestError(null);
      setDialogOpen(false);
      await loadRuns();
      await loadHealth();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleDownloadRunBundle = async () => {
    const targetRunId = selectedRunId === LIVE ? runId : selectedRunId;
    if (!targetRunId) return;
    setError(null);
    setDownloadingRun(true);
    try {
      await downloadTemplateQaRunBundle(targetRunId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingRun(false);
    }
  };

  const liveSummary = useMemo<TemplateQaRunSummary | null>(() => {
    if (!running && !(progress && selectedRunId === LIVE)) return null;
    const { passed, failed } = progressCounts(progress);
    return {
      runId: runId ?? LIVE,
      smoke: progress?.smoke,
      status: progress?.status,
      total: progress?.total,
      ok: passed,
      fail: failed,
      startedAt: progress?.startedAt,
      finishedAt: progress?.finishedAt,
    };
  }, [running, progress, selectedRunId, runId]);

  const elapsed = startedAt ? formatElapsed(Date.now() - startedAt) : null;
  const current = progress?.current ?? 0;
  const total = progress?.total ?? 0;
  const { passed: livePassed, failed: liveFail } = progressCounts(progress);
  const label = progressLabel(progress);
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  // Which run's results are shown in the grid?
  const viewingLive = selectedRunId === LIVE;
  const gridRunId = viewingLive ? runId : selectedRunId;
  const exportRunId = gridRunId;
  const gridVersion = viewingLive ? progress?.finishedAt : runs.find((r) => r.runId === selectedRunId)?.finishedAt;
  const gridManifest = viewingLive ? manifestFromProgress(progress) : archivedManifest;

  const okCount = gridManifest.filter((m) => m.ok).length;
  const failCount = gridManifest.filter((m) => !m.ok).length;
  const expectedImages = gridManifest.filter((m) => m.ok && m.file).length;
  const shown = filter === 'failed' ? gridManifest.filter((m) => !m.ok) : gridManifest;
  const groupedShown = useMemo(() => {
    const buckets = new Map<string, TemplateQaManifestEntry[]>();
    for (const entry of shown) {
      const key =
        groupMode === 'industry' ? (entry.industry ?? entry.vibe ?? 'unknown') : (entry.shell ?? 'unknown');
      const list = buckets.get(key);
      if (list) list.push(entry);
      else buckets.set(key, [entry]);
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, entries]) => ({ group, entries }));
  }, [shown, groupMode]);
  const hasSelection = selectedRunId !== null;

  const elapsedMs = startedAt ? Date.now() - startedAt : 0;

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <BlueprintLabel className="mb-1 block opacity-90">N°03 / TEMPLATE QA</BlueprintLabel>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-nm-ink sm:text-3xl">
            Template QA
          </h1>
          <p className="mt-1 max-w-2xl font-mono text-[11px] text-nm-muted">
            Screenshots every Flashpoint package × page × industry × look in master-scaffold. Local only — does not
            provision sites or create GitHub repos, GCP projects, or Cloudflare Pages.
          </p>
        </div>
        <a
          href={templateQaRunGalleryUrl(gridRunId)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-nm-muted underline-offset-2 hover:text-nm-accent hover:underline"
        >
          {gridRunId ? 'Open this run ↗' : 'Open gallery ↗'}
        </a>
      </header>

      {orchestratorOk === false && (
        <p className="rounded-sm border border-nm-warn/30 bg-nm-warn-bg px-3 py-2 text-sm text-nm-warn" role="alert">
          {error ?? 'Orchestrator unavailable. Check that orchestrator.logicbrooks.com is reachable.'}
        </p>
      )}

      <HealthPanel health={health} orchestratorOk={orchestratorOk} />

      <div className="rounded-xl border border-nm-border bg-nm-surface/90 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={running || orchestratorOk === false}
            onClick={() => void handleStart()}
            className="rounded-full bg-nm-ink px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-nm-surface transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Run stack smoke
          </button>
          {running && !dialogOpen && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="rounded-full border border-nm-border px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-nm-ink"
            >
              Show progress
            </button>
          )}
          <button
            type="button"
            disabled={running || orchestratorOk === false}
            onClick={() => void handleClear()}
            className="rounded-full border border-nm-border px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-nm-muted transition hover:border-nm-danger hover:text-nm-danger disabled:cursor-not-allowed disabled:opacity-40"
            title="Clear the current view. Keeps saved run history and screenshots."
          >
            Clear results
          </button>
        </div>

        {(running || progress) && (
          <div className="mt-4">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-nm-border/60">
              <div
                className={`h-full rounded-full transition-all duration-500 ${liveFail > 0 ? 'bg-nm-warn' : 'bg-nm-accent'}`}
                style={{ width: `${running ? pct : 100}%` }}
              />
            </div>
            <div className="mt-2 font-mono text-[11px] text-nm-muted">
              {running ? (
                <>
                  <span className="text-nm-ink">{progress?.status === 'starting' ? 'Warming up…' : 'Capturing…'}</span>{' '}
                  {total > 0 ? `${current}/${total}` : null}
                  {livePassed > 0 || liveFail > 0 ? ` · ${livePassed} passed, ${liveFail} failed` : null}
                  {label ? ` · ${label}` : null}
                  {elapsed ? ` · ${elapsed}` : null}
                  <span className="sr-only">{tick}</span>
                </>
              ) : progress?.status === 'failed' ? (
                <span className="text-nm-danger">{progress.message ?? error ?? 'Run failed'}</span>
              ) : progress?.status === 'done_with_failures' ? (
                <span className="text-nm-warn">
                  {livePassed} passed, {liveFail} failed{elapsed ? ` · ${elapsed}` : ''}
                </span>
              ) : progress?.status === 'done' ? (
                <span className="text-nm-success">
                  {livePassed} passed{elapsed ? ` · ${elapsed}` : ''}
                </span>
              ) : null}
            </div>
          </div>
        )}

        {error && orchestratorOk !== false && (
          <p className="mt-3 text-sm text-nm-danger" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-nm-border bg-nm-surface/90 p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <BlueprintLabel>Results dashboard</BlueprintLabel>
          <div className="flex flex-wrap items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-nm-muted">
              Run
              <select
                value={selectedRunId ?? ''}
                onChange={(e) => {
                  setSelectedRunId(e.target.value || null);
                  setFilter('all');
                }}
                className="ml-2 rounded-full border border-nm-border bg-nm-bg px-3 py-1.5 font-mono text-[10px] text-nm-ink"
              >
                <option value="">Select a run…</option>
                {liveSummary && (
                  <option value={LIVE}>
                    {running ? 'Live · running' : `Live · ${runOptionLabel(liveSummary)}`}
                  </option>
                )}
                {runs.map((r) => (
                  <option key={r.runId} value={r.runId}>
                    {runOptionLabel(r)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={!exportRunId || downloadingRun}
              onClick={() => void handleDownloadRunBundle()}
              className="rounded-full border border-nm-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-nm-ink disabled:cursor-not-allowed disabled:opacity-40"
              title="Download selected run screenshots + metadata as a zip bundle"
            >
              {downloadingRun ? 'Downloading…' : 'Download bundle'}
            </button>
            {gridManifest.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                  All · {gridManifest.length}
                </FilterChip>
                <FilterChip active={filter === 'failed'} onClick={() => setFilter('failed')} danger={failCount > 0}>
                  Failed · {failCount}
                </FilterChip>
                <FilterChip active={groupMode === 'industry'} onClick={() => setGroupMode('industry')}>
                  Group: industry
                </FilterChip>
                <FilterChip active={groupMode === 'package'} onClick={() => setGroupMode('package')}>
                  Group: package
                </FilterChip>
              </div>
            )}
          </div>
        </div>

        {manifestError && (
          <p className="mb-3 rounded-sm border border-nm-warn/30 bg-nm-warn-bg px-3 py-2 font-mono text-[11px] text-nm-warn" role="alert">
            {manifestError}
          </p>
        )}

        {gridManifest.length > 0 && (
          <div className="mb-3 space-y-2">
            <StatusStrip entries={gridManifest} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] text-nm-muted">
              <span>
                <span className="text-nm-success">{okCount} passed</span>
                {failCount > 0 ? <span className="text-nm-danger"> · {failCount} failed</span> : null}
                {` · ${gridManifest.length} combos`}
              </span>
              {expectedImages > 0 && (
                <span title="Thumbnails that actually loaded vs failed in your browser">
                  images:{' '}
                  <span className={imgLoaded > 0 ? 'text-nm-success' : 'text-nm-muted'}>{imgLoaded} loaded</span>
                  {imgFailed > 0 ? <span className="text-nm-danger"> · {imgFailed} failed</span> : null}
                  {` / ${expectedImages}`}
                </span>
              )}
              {imgFailed > 0 && (
                <span className="text-nm-warn">
                  Images failing? Restart pnpm dev so the Vite /qa proxy is active.
                </span>
              )}
            </div>
          </div>
        )}

        {!hasSelection ? (
          <p className="rounded-lg border border-dashed border-nm-border p-10 text-center font-mono text-[11px] text-nm-muted">
            {runs.length > 0
              ? 'Select a run above to view its screenshots, or start a new run.'
              : 'No runs yet. Click Run stack smoke, then Download bundle for the zip.'}
          </p>
        ) : gridManifest.length === 0 ? (
          <p className="rounded-lg border border-dashed border-nm-border p-10 text-center font-mono text-[11px] text-nm-muted">
            {running ? 'Capturing… screenshots will appear here as each combo finishes.' : 'No screenshots for this run.'}
          </p>
        ) : shown.length === 0 ? (
          <p className="rounded-lg border border-dashed border-nm-border p-6 text-center font-mono text-[11px] text-nm-muted">
            {`No failures — all ${okCount} captured combos passed.`}
          </p>
        ) : (
          <div className="space-y-4">
            {groupedShown.map(({ group, entries }) => {
              const groupOk = entries.filter((m) => m.ok).length;
              const groupFail = entries.length - groupOk;
              return (
                <section key={group} className="rounded-lg border border-nm-border/70 bg-nm-bg/20 p-3">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-nm-ink">
                      {groupMode === 'industry' ? 'Industry' : 'Package'} · {group}
                    </h3>
                    <p className="font-mono text-[10px] text-nm-muted">
                      {groupOk} passed{groupFail > 0 ? ` · ${groupFail} failed` : ''} · {entries.length} items
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {entries.map((m) => (
                      <ResultCard
                        key={m.slug}
                        entry={m}
                        runId={gridRunId}
                        version={gridVersion}
                        onImgLoad={() => setImgLoaded((n) => n + 1)}
                        onImgError={() => setImgFailed((n) => n + 1)}
                        onOpen={() =>
                          m.ok && gridRunId && setLightbox({ entry: m, runId: gridRunId, version: gridVersion })
                        }
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <p className="font-mono text-[10px] text-nm-muted/80">
        CLI: <code className="rounded bg-nm-bg px-1">pnpm --filter master-scaffold qa:matrix</code> · results in{' '}
        <code className="rounded bg-nm-bg px-1">master-scaffold/.previews/runs/</code>
      </p>
      <p className="font-mono text-[10px] text-nm-muted/70">
        Bundle download includes run images + <code className="rounded bg-nm-bg px-1">manifest.json</code> +{' '}
        <code className="rounded bg-nm-bg px-1">run-summary.json</code> for copy/paste issue reporting.
      </p>

      {lightbox?.entry.file && (
        <button
          type="button"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 flex flex-col items-center justify-start overflow-auto bg-black/80 p-6"
          aria-label="Close preview"
        >
          <div className="mb-3 font-mono text-[11px] text-white">
            {lightbox.entry.label ?? `${lightbox.entry.shell} / ${lightbox.entry.vibe}`} — click anywhere to close
          </div>
          <img
            src={templateQaImageUrl(lightbox.runId, lightbox.entry.file, lightbox.version)}
            alt={lightbox.entry.label ?? `${lightbox.entry.shell} / ${lightbox.entry.vibe}`}
            className="max-w-4xl rounded-lg border border-white/20 bg-white"
          />
        </button>
      )}

      <SmokeProgressDialog
        open={dialogOpen}
        running={running}
        progress={progress}
        elapsedMs={elapsedMs}
        fatalError={progress?.status === 'failed' ? (progress.message ?? error) : running ? null : error}
        downloading={downloadingRun}
        onHide={() => setDialogOpen(false)}
        onClose={() => setDialogOpen(false)}
        onDownload={() => void handleDownloadRunBundle()}
      />
    </section>
  );
}

function StatusPill({
  label,
  ok,
  detail,
  neutral,
}: {
  label: string;
  ok: boolean;
  detail?: string;
  neutral?: boolean;
}) {
  const tone = neutral
    ? 'border-nm-border text-nm-muted'
    : ok
      ? 'border-nm-success/30 bg-nm-success-bg text-nm-success'
      : 'border-nm-danger/30 bg-nm-danger-bg text-nm-danger';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] ${tone}`}
      title={detail}
    >
      {!neutral && (
        <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-nm-success' : 'bg-nm-danger'}`} aria-hidden />
      )}
      <span className="font-semibold uppercase tracking-wide">{label}</span>
      {detail && <span className="opacity-80">{detail}</span>}
    </span>
  );
}

function HealthPanel({
  health,
  orchestratorOk,
}: {
  health: TemplateQaHealth | null;
  orchestratorOk: boolean | null;
}) {
  if (!health) {
    return (
      <div className="rounded-xl border border-nm-border bg-nm-surface/90 p-5">
        <BlueprintLabel>Health &amp; status</BlueprintLabel>
        <p className="mt-2 font-mono text-[11px] text-nm-muted">
          {orchestratorOk === false
            ? 'Orchestrator unreachable — start pnpm dev to see tool health.'
            : 'Loading tool health…'}
        </p>
      </div>
    );
  }

  const last = health.runs.lastRun;
  const files = health.runs.lastRunFiles;

  return (
    <div className="rounded-xl border border-nm-border bg-nm-surface/90 p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <BlueprintLabel>Health &amp; status</BlueprintLabel>
        <span className="font-mono text-[10px] text-nm-muted/80">
          checked {formatRunTime(health.checkedAt)}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <StatusPill
          label="Orchestrator"
          ok={health.orchestrator.up}
          detail={`:${health.orchestrator.port} · node ${health.orchestrator.nodeVersion}`}
        />
        <StatusPill
          label="Chromium"
          ok={health.browser.chromiumInstalled}
          detail={health.browser.chromiumInstalled ? 'installed' : 'missing — run installs on first use'}
        />
        <StatusPill label="QA script" ok={health.script.exists} detail={health.script.exists ? 'found' : 'missing'} />
        <StatusPill
          label="Previews dir"
          ok={health.previews.exists}
          detail={health.previews.exists ? 'ready' : 'missing'}
        />
        <StatusPill
          label={health.runs.running ? 'Run in progress' : 'Idle'}
          ok
          neutral={!health.runs.running}
          detail={health.runs.running ? (health.runs.activeRunId ?? undefined) : `${health.runs.count} runs saved`}
        />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-[11px] sm:grid-cols-4">
        <HealthStat label="Saved runs" value={String(health.runs.count)} />
        <HealthStat
          label="Last run"
          value={last ? `${last.ok ?? 0}/${last.total ?? 0} ok` : '—'}
          sub={last ? formatRunTime(last.finishedAt ?? last.startedAt) : undefined}
          tone={last ? (last.fail ? 'warn' : 'ok') : undefined}
        />
        <HealthStat
          label="Last run files"
          value={files ? `${files.pngCount} png` : '—'}
          sub={files ? `${formatBytes(files.bytes)}${files.hasManifest ? ' · manifest ✓' : ' · no manifest'}` : undefined}
          tone={files && !files.hasManifest ? 'warn' : undefined}
        />
        <HealthStat
          label="Stale top-level"
          value={`${health.previews.staleTopLevelPng} png`}
          sub={health.previews.staleTopLevelPng > 0 ? 'pre-archive leftovers' : 'clean'}
          tone={health.previews.staleTopLevelPng > 0 ? 'warn' : undefined}
        />
      </dl>

      <p className="mt-3 truncate font-mono text-[10px] text-nm-muted/70" title={health.previews.dir}>
        {health.previews.dir}
      </p>
    </div>
  );
}

function HealthStat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
}) {
  const valueTone = tone === 'warn' ? 'text-nm-warn' : tone === 'ok' ? 'text-nm-success' : 'text-nm-ink';
  return (
    <div>
      <dt className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">{label}</dt>
      <dd className={`mt-0.5 font-semibold ${valueTone}`}>{value}</dd>
      {sub && <dd className="text-[10px] text-nm-muted">{sub}</dd>}
    </div>
  );
}

/** Compact dot-per-combo overview so status is visible even if thumbnails fail to load. */
function StatusStrip({ entries }: { entries: TemplateQaManifestEntry[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map((m) => (
        <span
          key={m.slug}
          title={comboDisplayLabel(m)}
          className={`h-2.5 w-2.5 rounded-[2px] ${m.ok ? 'bg-nm-success/80' : 'bg-nm-danger'}`}
        />
      ))}
    </div>
  );
}

interface FilterChipProps {
  active: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function FilterChip({ active, danger, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-wide transition ${
        active
          ? danger
            ? 'bg-nm-danger/15 text-nm-danger ring-1 ring-nm-danger/30'
            : 'bg-nm-accent/20 text-nm-accent ring-1 ring-nm-accent/40'
          : 'border border-nm-border text-nm-muted hover:border-nm-muted hover:text-nm-ink'
      }`}
    >
      {children}
    </button>
  );
}

function ResultCard({
  entry,
  runId,
  version,
  onOpen,
  onImgLoad,
  onImgError,
}: {
  entry: TemplateQaManifestEntry;
  runId: string | null;
  version?: string;
  onOpen: () => void;
  onImgLoad: () => void;
  onImgError: () => void;
}) {
  return (
    <figure
      className={`group m-0 overflow-hidden rounded-lg border ${
        entry.ok ? 'border-nm-border bg-nm-bg/40' : 'border-nm-danger/30 bg-nm-danger-bg'
      }`}
    >
      {entry.ok && entry.file && runId ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full cursor-zoom-in"
          title={comboDisplayLabel(entry)}
        >
          <img
            src={templateQaImageUrl(runId, entry.file, version)}
            loading="lazy"
            alt={comboDisplayLabel(entry)}
            onLoad={onImgLoad}
            onError={onImgError}
            className="aspect-[16/11] w-full bg-white object-cover object-top transition group-hover:opacity-90"
          />
        </button>
      ) : (
        <div className="flex aspect-[16/11] w-full items-center justify-center p-3 text-center font-mono text-[10px] text-nm-danger">
          {entry.error ?? 'failed'}
        </div>
      )}
      <figcaption className="flex items-baseline justify-between gap-2 px-2.5 py-1.5 font-mono text-[10px]">
        <span className={`font-semibold ${entry.ok ? 'text-nm-ink' : 'text-nm-danger'}`}>{entry.shell}</span>
        <span className="truncate text-nm-muted">{entry.label ?? entry.vibe}</span>
      </figcaption>
    </figure>
  );
}
